import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  F0Box,
  SurveyFormBuilder,
  SurveyFormBuilderElement,
  QuestionType,
} from '@factorialco/f0-react'
import isEqual from 'lodash/isEqual'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'

import { useOne, useOneFeatureAvailable } from 'modules/ai/lib/useOne'
import {
  FromFormToQuestionnaire,
  FromQuestionnaireToForm,
} from 'modules/forms/components/newFormEditor/helpers'
import { AgentStateType, QuestionnaireType } from 'modules/forms/components/newFormEditor/types'
import { CreateVendorFromDropdownModal } from 'modules/procurement/components/CreateVendorFromDropdownModal'
import { usePurchaseRequestDatasets } from 'modules/procurement/components/purchaseRequests/PurchaseRequestForm/datasets'
import i18n from 'modules/core/lib/i18n'

const ALLOWED_QUESTION_TYPES: QuestionType[] = [
  'text',
  'longText',
  'select',
  'multi-select',
  'numeric',
  'link',
  'date',
  'file',
  'checkbox',
]

// The SurveyFormBuilder doesn't carry invariantId or element params (fieldIdentifier, scoreType, etc.),
// so we need to keep track of them separately and restore after round-tripping through the builder.
function collectInvariantIds(elements: QuestionnaireType['elements']): Map<string, string> {
  const map = new Map<string, string>()
  for (const el of elements) {
    if (el.id && el.invariantId) map.set(el.id, el.invariantId)
    if ('elements' in el) {
      for (const child of el.elements) {
        if (child.id && child.invariantId) map.set(child.id, child.invariantId)
      }
    }
  }
  return map
}

type ElementParams = QuestionnaireType['elements'][number]['params']

function collectElementParams(elements: QuestionnaireType['elements']): Map<string, ElementParams> {
  const map = new Map<string, ElementParams>()
  for (const el of elements) {
    if (el.id && el.params && Object.keys(el.params).length > 0) map.set(el.id, el.params)
    if ('elements' in el) {
      for (const child of el.elements) {
        if (child.id && child.params && Object.keys(child.params).length > 0)
          map.set(child.id, child.params)
      }
    }
  }
  return map
}

// Dataset-bound questions are sourced from a catalog (e.g. vendors) and map to
// fixed top-level fields in the purchase-request payload. Mutating them from the
// editor produces a malformed request or silent data loss:
//   - changing the type desynchronises type <-> dataset binding (FCT-54721 root cause)
//   - enabling multi-select corrupts the payload (BE expects a scalar)
//   - duplicating creates two questions mapping to the same field (last-write-wins)
// So we hide questionType, multiSelect and duplicate for those elements.
function collectDatasetBoundIds(elements: QuestionnaireType['elements']): Set<string> {
  const ids = new Set<string>()
  for (const el of elements) {
    if (el.id && el.params?.dataset) ids.add(el.id)
    if ('elements' in el) {
      for (const child of el.elements) {
        if (child.id && child.params?.dataset) ids.add(child.id)
      }
    }
  }
  return ids
}

const DATASET_BOUND_HIDDEN_ACTIONS = ['questionType', 'multiSelect', 'duplicate'] as const

interface Props {
  state: QuestionnaireType
  setState: (state: QuestionnaireType) => void
  coCreationKey?: string
}

const FormEditor = ({ state, setState, coCreationKey }: Props) => {
  const { company } = useCurrentSession()
  const { datasets, createVendorModal, onVendorCreated, onCreateVendorModalClose } =
    usePurchaseRequestDatasets(company.id, { enableVendorCreation: true })
  const directUpload = useDirectUpload({ resourceType: 'Procurement::Type' })
  const oneAvailable = useOneFeatureAvailable()

  const initialAgentState: AgentStateType = useMemo(
    () => ({
      questionnaire: state,
      updateForm: true,
    }),
    // Only recompute when coCreationKey changes (form/version switch), not on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coCreationKey]
  )

  const oneOptions = useMemo(
    () => ({
      autoOpenChat: true,
      initialMessage: i18n.t('procurement.types.one.chat_initial_message'),
      fullscreen: false,
      coCreationKey,
    }),
    [coCreationKey]
  )

  const {
    coCreationData: agentState,
    setCoCreationData: setAgentState,
    isThinking,
  } = useOne<AgentStateType>(
    oneAvailable ? ['procurementTypeForm'] : [],
    initialAgentState,
    oneAvailable ? oneOptions : { enabled: false }
  )

  // Bidirectional sync between parent state and agent state.
  // Track previous values to detect which side changed and sync accordingly.
  const prevElementsRef = useRef<{
    state: QuestionnaireType['elements']
    agent: QuestionnaireType['elements'] | undefined
  }>({
    state: state.elements,
    agent: agentState?.questionnaire?.elements,
  })

  useEffect(() => {
    const prev = prevElementsRef.current
    prevElementsRef.current = {
      state: state.elements,
      agent: agentState?.questionnaire?.elements,
    }

    if (!oneAvailable || !agentState?.questionnaire) return
    if (isEqual(state.elements, agentState.questionnaire.elements)) return

    const parentChanged = !isEqual(state.elements, prev.state)
    const agentChanged = !isEqual(agentState.questionnaire.elements, prev.agent)

    if (parentChanged) {
      // Parent updated (query resolved, version restored) → push to agent
      setAgentState({ ...agentState, questionnaire: state, updateForm: true })
    } else if (agentChanged) {
      // AI updated → push to parent
      setState({ ...state, elements: agentState.questionnaire.elements })
    }
  }, [oneAvailable, agentState, state, setState, setAgentState])

  // The active questionnaire: agent state when One is available, parent state otherwise.
  const activeQuestionnaire =
    oneAvailable && agentState?.questionnaire ? agentState.questionnaire : state

  const useUpload = useCallback(
    () => ({
      upload: async (file: File) => {
        const result = await directUpload.upload(file)
        if (result.type === 'aborted') return { type: 'aborted' as const }
        return { type: 'success' as const, value: result.signedId }
      },
      cancelUpload: directUpload.cancelUpload,
      progress: directUpload.progress,
      status: directUpload.status,
    }),
    [directUpload]
  )

  const elements = useMemo(() => {
    const built = FromQuestionnaireToForm(activeQuestionnaire.elements, {
      mapDropdownSingleTo: 'dropdown-single',
    })
    const datasetBoundIds = collectDatasetBoundIds(activeQuestionnaire.elements)
    if (datasetBoundIds.size === 0) return built
    return built.map((el) => {
      if (el.type === 'section') {
        return {
          ...el,
          section: {
            ...el.section,
            questions: el.section.questions?.map((q) =>
              q.id && datasetBoundIds.has(q.id)
                ? { ...q, hiddenActions: DATASET_BOUND_HIDDEN_ACTIONS }
                : q
            ),
          },
        }
      }
      if (el.question.id && datasetBoundIds.has(el.question.id)) {
        return {
          ...el,
          question: { ...el.question, hiddenActions: DATASET_BOUND_HIDDEN_ACTIONS },
        }
      }
      return el
    })
  }, [activeQuestionnaire.elements])

  const invariantIds = useMemo(
    () => collectInvariantIds(activeQuestionnaire.elements),
    [activeQuestionnaire.elements]
  )
  const savedParams = useMemo(
    () => collectElementParams(activeQuestionnaire.elements),
    [activeQuestionnaire.elements]
  )

  const handleChange = (updatedElements: SurveyFormBuilderElement[]) => {
    const converted = FromFormToQuestionnaire(updatedElements)

    for (const el of converted) {
      el.invariantId = invariantIds.get(el.id ?? '') ?? el.invariantId
      const original = savedParams.get(el.id ?? '')
      if (original) el.params = { ...original, ...el.params }
      if ('elements' in el) {
        for (const child of el.elements) {
          child.invariantId = invariantIds.get(child.id ?? '') ?? child.invariantId
          const childOriginal = savedParams.get(child.id ?? '')
          if (childOriginal) child.params = { ...childOriginal, ...child.params }
        }
      }
    }

    const updatedQuestionnaire = { ...activeQuestionnaire, elements: converted }
    setState(updatedQuestionnaire)

    if (oneAvailable && agentState) {
      setAgentState({ ...agentState, questionnaire: updatedQuestionnaire, updateForm: true })
    }
  }

  return (
    <>
      <F0Box width="full" height="full" padding="md" paddingBottom="5xl">
        <SurveyFormBuilder
          elements={elements}
          onChange={handleChange}
          allowedQuestionTypes={ALLOWED_QUESTION_TYPES}
          useUpload={useUpload}
          datasets={datasets}
          applyingChanges={oneAvailable && isThinking}
        />
      </F0Box>
      {createVendorModal.isOpen && (
        <CreateVendorFromDropdownModal
          companyId={company.id}
          initialName={createVendorModal.initialName}
          onClose={onCreateVendorModalClose}
          onCreated={onVendorCreated}
        />
      )}
    </>
  )
}

export default FormEditor
